<template>
  <div>
    <!-- Page Header -->
    <CCard class="mb-4 border-0 shadow-sm">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="6">
            <h1 class="h4 mb-1 font-weight-bold">Payment vouchers</h1>
            <p class="text-muted mb-0">
              Manage and authorise payments to vendors and suppliers
            </p>
          </CCol>
          <CCol md="6" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton color="light" class="mr-2" aria-label="Refresh" title="Refresh" @click="refreshList">
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Create payment voucher
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <!-- Payment Voucher List -->
    <CCard class="mb-4 border-0 shadow-sm">
      <CCardHeader class="bg-transparent border-0 p-4">
        <CRow class="align-items-center">
          <CCol>
            <h5 class="mb-0">
              Vouchers
              <CBadge color="light" class="ml-2">{{ filteredItems.length }}</CBadge>
            </h5>
          </CCol>
          <CCol class="d-flex justify-content-end">
            <CButton color="light" size="sm" @click="showFilters = !showFilters">
              <CIcon :name="showFilters ? 'cil-chevron-top' : 'cil-filter'" />
              {{ showFilters ? "Hide filters" : "Show filters" }}
            </CButton>
            <CBadge v-if="hasActiveFilters" color="primary" class="ml-2 align-self-center">
              {{ activeFiltersCount }} active
            </CBadge>
          </CCol>
        </CRow>
      </CCardHeader>
      <CCardBody class="p-4">
        <!-- Filters (apply instantly) -->
        <CCollapse :show="showFilters">
          <CCard class="mb-4 border">
            <CCardBody>
              <CRow>
                <CCol md="3">
                  <CInput
                    label="Voucher number"
                    v-model="filters.voucherNumber"
                    placeholder="Search voucher no."
                  />
                </CCol>
                <CCol md="3">
                  <CInput
                    label="Vendor"
                    v-model="filters.vendorName"
                    placeholder="Search vendor"
                  />
                </CCol>
                <CCol md="3">
                  <CSelect
                    label="Status"
                    :value.sync="filters.status"
                    :options="statusFilterOptions"
                  />
                </CCol>
                <CCol md="3">
                  <CSelect
                    label="Payment method"
                    :value.sync="filters.paymentMethod"
                    :options="[
                      { value: '', label: 'All methods' },
                      { value: 'Bank Transfer', label: 'Bank transfer' },
                      { value: 'Check', label: 'Cheque' },
                      { value: 'Cash', label: 'Cash' },
                      { value: 'Online Payment', label: 'Online payment' }
                    ]"
                  />
                </CCol>
              </CRow>
              <CRow class="mt-3">
                <CCol class="d-flex justify-content-end">
                  <CButton color="light" @click="resetFilters">
                    <CIcon name="cil-x" /> Clear filters
                  </CButton>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCollapse>

        <!-- Loading (first load) -->
        <div v-if="loading && !items.length" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading…</span>
          </div>
          <p class="mt-3 text-muted">Loading payment vouchers…</p>
        </div>

        <!-- Error -->
        <div v-else-if="loadError" class="text-center py-5">
          <CIcon name="cil-warning" size="2xl" class="mb-3 text-danger" />
          <h5>Couldn't load payment vouchers</h5>
          <p class="text-muted mb-3">Check your connection and try again.</p>
          <CButton color="primary" @click="loadItems">
            <CIcon name="cil-reload" /> Try again
          </CButton>
        </div>

        <!-- Empty (first use) -->
        <div v-else-if="!items.length" class="text-center py-5">
          <CIcon name="cil-inbox" size="2xl" class="mb-3 text-muted" />
          <h5>No payment vouchers yet</h5>
          <p class="text-muted mb-3">Create a voucher to get a payment to a vendor approved and recorded.</p>
          <CButton color="primary" @click="addNew">
            <CIcon name="cil-plus" /> Create payment voucher
          </CButton>
        </div>

        <!-- Empty (filtered) -->
        <div v-else-if="!filteredItems.length" class="text-center py-5">
          <CIcon name="cil-filter-x" size="2xl" class="mb-3 text-muted" />
          <h5>No payment vouchers match your filters</h5>
          <CButton color="light" class="mt-2" @click="resetFilters">
            <CIcon name="cil-x" /> Clear filters
          </CButton>
        </div>

        <template v-else>
          <!-- Desktop table -->
          <div class="d-none d-lg-block">
            <CDataTable
              :items="filteredItems"
              :fields="fields"
              :items-per-page="25"
              :loading="loading"
              hover
              pagination
              sorter
              items-per-page-select
              clickable-rows
              @row-clicked="rowClicked"
            >
              <template #voucherNumber="{ item }">
                <td>
                  <div class="font-weight-bold text-monospace">{{ item.voucherNumber }}</div>
                  <div class="small text-muted">
                    {{ formatDate(item.voucherDate) }}
                  </div>
                </td>
              </template>

              <template #vendor="{ item }">
                <td>
                  <div v-if="item.vendor">
                    {{ item.vendor.name }}
                  </div>
                  <div v-else class="text-muted">{{ item.payeeName || '-' }}</div>
                </td>
              </template>

              <template #totalAmount="{ item }">
                <td class="text-right">
                  <div class="font-weight-bold">
                    {{ formatCurrency(item.totalAmount) }}
                  </div>
                </td>
              </template>

              <template #paymentMethod="{ item }">
                <td>
                  <div>{{ item.paymentMethod || '-' }}</div>
                  <div class="small text-muted" v-if="item.paymentReference">
                    Ref: {{ item.paymentReference }}
                  </div>
                </td>
              </template>

              <template #status="{ item }">
                <td>
                  <CBadge :color="badgeColor('paymentVoucher', item.statusDescription)">
                    {{ badgeLabel('paymentVoucher', item.statusDescription) }}
                  </CBadge>
                </td>
              </template>

              <template #paymentDate="{ item }">
                <td>
                  {{ item.paymentDate ? formatDate(item.paymentDate) : '-' }}
                </td>
              </template>

              <template #actions="{ item }">
                <td class="text-center" @click.stop>
                  <CDropdown color="link" size="sm" placement="bottom-end">
                    <template #toggler-content>
                      <CIcon name="cil-options" />
                      <span class="sr-only">More actions for {{ item.voucherNumber }}</span>
                    </template>
                    <CDropdownItem @click="viewVoucher(item)">
                      <CIcon name="cil-pencil" class="mr-2" /> Edit
                    </CDropdownItem>
                    <CDropdownItem
                      @click="askAction('approve', item)"
                      v-if="item.statusDescription === 'PendingApproval'"
                    >
                      <CIcon name="cil-check" class="mr-2" /> Approve
                    </CDropdownItem>
                    <CDropdownItem
                      @click="askAction('reject', item)"
                      v-if="item.statusDescription === 'PendingApproval'"
                    >
                      <CIcon name="cil-x" class="mr-2" /> Reject
                    </CDropdownItem>
                    <CDropdownItem
                      @click="askAction('pay', item)"
                      v-if="item.statusDescription === 'Approved'"
                    >
                      <CIcon name="cil-dollar" class="mr-2" /> Mark as paid
                    </CDropdownItem>
                    <template v-if="item.statusDescription === 'Draft'">
                      <CDropdownDivider />
                      <CDropdownItem @click="askAction('delete', item)" class="text-danger">
                        <CIcon name="cil-trash" class="mr-2" /> Delete
                      </CDropdownItem>
                    </template>
                  </CDropdown>
                </td>
              </template>
            </CDataTable>
          </div>

          <!-- Mobile cards -->
          <div class="d-lg-none">
            <CCard v-for="item in filteredItems" :key="item.id" class="mb-3 border">
              <CCardHeader class="d-flex justify-content-between align-items-center py-2 bg-light">
                <strong class="text-monospace">{{ item.voucherNumber }}</strong>
                <CBadge :color="badgeColor('paymentVoucher', item.statusDescription)">
                  {{ badgeLabel('paymentVoucher', item.statusDescription) }}
                </CBadge>
              </CCardHeader>
              <CCardBody class="py-2">
                <div class="d-flex justify-content-between mb-1">
                  <div class="text-muted small">Payee</div>
                  <div class="small">{{ (item.vendor && item.vendor.name) || item.payeeName || '-' }}</div>
                </div>
                <div class="d-flex justify-content-between mb-1">
                  <div class="text-muted small">Date</div>
                  <div class="small">{{ formatDate(item.voucherDate) }}</div>
                </div>
                <div class="d-flex justify-content-between mb-1">
                  <div class="text-muted small">Amount</div>
                  <div class="font-weight-bold text-right">{{ formatCurrency(item.totalAmount) }}</div>
                </div>
              </CCardBody>
              <CCardFooter class="py-2 d-flex justify-content-between bg-white">
                <CButton color="light" size="sm" @click="viewVoucher(item)">
                  <CIcon name="cil-pencil" /> Edit
                </CButton>
                <CDropdown
                  v-if="item.statusDescription === 'PendingApproval' || item.statusDescription === 'Approved' || item.statusDescription === 'Draft'"
                  size="sm"
                  color="light"
                  placement="bottom-end"
                >
                  <template #toggler-content>
                    <CIcon name="cil-options" /> More
                  </template>
                  <CDropdownItem v-if="item.statusDescription === 'PendingApproval'" @click="askAction('approve', item)">Approve</CDropdownItem>
                  <CDropdownItem v-if="item.statusDescription === 'PendingApproval'" @click="askAction('reject', item)">Reject</CDropdownItem>
                  <CDropdownItem v-if="item.statusDescription === 'Approved'" @click="askAction('pay', item)">Mark as paid</CDropdownItem>
                  <template v-if="item.statusDescription === 'Draft'">
                    <CDropdownDivider />
                    <CDropdownItem class="text-danger" @click="askAction('delete', item)">Delete</CDropdownItem>
                  </template>
                </CDropdown>
              </CCardFooter>
            </CCard>
          </div>
        </template>
      </CCardBody>
    </CCard>

    <!-- Action confirmation (approve / reject / mark paid / delete) -->
    <CModal
      :title="confirmTitle"
      :show.sync="confirm.show"
      :close-on-backdrop="!confirm.busy"
    >
      <p class="mb-0">{{ confirmMessage }}</p>
      <template #footer>
        <CButton color="light" :disabled="confirm.busy" @click="confirm.show = false">
          {{ confirm.kind === "delete" || confirm.kind === "reject" ? "Keep it" : "Cancel" }}
        </CButton>
        <CButton
          :color="confirm.kind === 'delete' || confirm.kind === 'reject' ? 'danger' : 'primary'"
          :disabled="confirm.busy"
          @click="runAction"
        >
          <CSpinner v-if="confirm.busy" size="sm" class="mr-1" />
          {{ confirmButton }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import paymentVoucherApi from "@/lib/paymentVoucherApi";
import { formatMyr } from "@/utils/money";
import { formatDate } from "@/utils/datetime";
import { badgeColor, badgeLabel } from "@/utils/statusBadges";

const STATUSES = ["Draft", "PendingApproval", "Approved", "Paid", "Reconciled", "Rejected", "Voided"];

export default {
  name: "PaymentVoucherList",
  data() {
    return {
      items: [],
      loading: false,
      loadError: false,
      showFilters: false,
      confirm: { show: false, kind: "", item: null, busy: false },
      filters: {
        voucherNumber: "",
        vendorName: "",
        status: "",
        paymentMethod: "",
      },
      fields: [
        {
          key: "voucherNumber",
          label: "Voucher no.",
          _style: "width: 15%;",
        },
        {
          key: "vendor",
          label: "Vendor",
          _style: "width: 20%;",
        },
        {
          key: "totalAmount",
          label: "Amount",
          _style: "width: 12%; text-align: right;",
        },
        {
          key: "paymentMethod",
          label: "Payment method",
          _style: "width: 15%;",
        },
        {
          key: "paymentDate",
          label: "Payment date",
          _style: "width: 12%;",
        },
        {
          key: "status",
          label: "Status",
          _style: "width: 12%;",
        },
        {
          key: "actions",
          label: "",
          _style: "width: 5%;",
          sorter: false,
          filter: false,
        },
      ],
    };
  },
  computed: {
    statusFilterOptions() {
      return [{ value: "", label: "All statuses" }].concat(
        STATUSES.map((s) => ({ value: s, label: badgeLabel("paymentVoucher", s) }))
      );
    },
    hasActiveFilters() {
      return Object.values(this.filters).some((v) => v !== "");
    },
    activeFiltersCount() {
      return Object.values(this.filters).filter((v) => v !== "").length;
    },
    filteredItems() {
      let items = this.items || [];

      if (this.filters.voucherNumber) {
        items = items.filter((item) =>
          item.voucherNumber &&
          item.voucherNumber
            .toLowerCase()
            .includes(this.filters.voucherNumber.toLowerCase())
        );
      }

      if (this.filters.vendorName) {
        items = items.filter(
          (item) =>
            item.vendor &&
            item.vendor.name &&
            item.vendor.name
              .toLowerCase()
              .includes(this.filters.vendorName.toLowerCase())
        );
      }

      if (this.filters.status) {
        items = items.filter(
          (item) => item.statusDescription === this.filters.status
        );
      }

      if (this.filters.paymentMethod) {
        items = items.filter(
          (item) => item.paymentMethod === this.filters.paymentMethod
        );
      }

      return items;
    },
    confirmTitle() {
      const item = this.confirm.item || {};
      const no = item.voucherNumber ? ` ${item.voucherNumber}` : "";
      switch (this.confirm.kind) {
        case "approve": return `Approve payment voucher${no}?`;
        case "reject": return `Reject payment voucher${no}?`;
        case "pay": return `Mark payment voucher${no} as paid?`;
        case "delete": return `Delete payment voucher${no}?`;
        default: return "";
      }
    },
    confirmMessage() {
      const item = this.confirm.item || {};
      const amount = this.formatCurrency(item.totalAmount);
      const payee = (item.vendor && item.vendor.name) || item.payeeName || "the payee";
      switch (this.confirm.kind) {
        case "approve": return `The ${amount} payment to ${payee} will be approved and can then be paid.`;
        case "reject": return `The ${amount} payment to ${payee} will be rejected and won't be paid.`;
        case "pay": return `The ${amount} payment to ${payee} will be recorded as paid in your accounts.`;
        case "delete": return `The draft voucher for ${amount} to ${payee} will be removed. This can't be undone.`;
        default: return "";
      }
    },
    confirmButton() {
      switch (this.confirm.kind) {
        case "approve": return "Approve voucher";
        case "reject": return "Reject voucher";
        case "pay": return "Mark as paid";
        case "delete": return "Delete voucher";
        default: return "Continue";
      }
    },
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Expenses", "Payment Vouchers"]);
    this.loadItems();
  },
  methods: {
    badgeColor,
    badgeLabel,
    async loadItems() {
      this.loading = true;
      this.loadError = false;
      try {
        const data = await paymentVoucherApi.getListByCurrentBusiness();
        this.items = data.result || [];
      } catch (error) {
        console.error("Failed to load payment vouchers:", error);
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },
    addNew() {
      this.$router.push({ name: "PaymentVoucher" });
    },
    rowClicked(item) {
      this.viewVoucher(item);
    },
    viewVoucher(item) {
      this.$router.push({
        name: "PaymentVoucherById",
        params: { id: item.id },
      });
    },
    askAction(kind, item) {
      this.confirm = { show: true, kind, item, busy: false };
    },
    async runAction() {
      const { kind, item } = this.confirm;
      if (!item || this.confirm.busy) return;
      this.confirm.busy = true;
      const actions = {
        approve: { call: () => paymentVoucherApi.approve(item.id), done: "Payment voucher approved", fail: "Couldn't approve the voucher." },
        reject: { call: () => paymentVoucherApi.reject(item.id), done: "Payment voucher rejected", fail: "Couldn't reject the voucher." },
        pay: { call: () => paymentVoucherApi.markAsPaid(item.id), done: "Payment voucher marked as paid", fail: "Couldn't mark the voucher as paid." },
        delete: { call: () => paymentVoucherApi.delete(item.id), done: "Payment voucher deleted", fail: "Couldn't delete the voucher." },
      };
      const action = actions[kind];
      try {
        await action.call();
        this.$toast.success(action.done);
        this.confirm.show = false;
        this.loadItems();
      } catch (error) {
        this.$toast.error(`${action.fail} ${helper.getErrorMessage(error && error.data, "Please try again.")}`);
      } finally {
        this.confirm.busy = false;
      }
    },
    refreshList() {
      this.loadItems();
    },
    resetFilters() {
      this.filters = {
        voucherNumber: "",
        vendorName: "",
        status: "",
        paymentMethod: "",
      };
    },
    formatDate(date) {
      return date ? formatDate(date) : "-";
    },
    formatCurrency(amount) {
      return `RM ${formatMyr(amount)}`;
    },
  },
};
</script>
